import React, { useEffect, useMemo, useState, useImperativeHandle, forwardRef } from 'react';
import { Typography, Spin, Card } from 'antd';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { Table } from 'antd';
import StatsKPIGroup, { KPIItem } from '../Business/StatsKPIGroup';
import { ForgeQueryBuilder, type FieldConfig, type ForgeQuery } from '../ForgeQueryBuilder';
import type { PaginationInfo } from '../../types/base.dto';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../types/base.dto';

export type ListRef = {
  reload: () => Promise<void>;
  search: (q: ForgeQuery) => Promise<void>;
};

type ListProps<TRow, TRequest, TStats> = {
  title?: React.ReactNode;
  queryFields: FieldConfig[];
  fetcher: (params: TRequest) => Promise<{ rows: TRow[]; stats?: TStats; pagination?: PaginationInfo }>;
  buildParams?: (q: ForgeQuery) => TRequest;
  defaultQuery?: ForgeQuery;
  kpiItems?: KPIItem[] | ((stats: TStats) => KPIItem[]);
  actions?: React.ReactNode[];
  columns: ColumnsType<TRow>;
  rowKey?: (r: TRow) => string;
  tableProps?: TableProps<TRow>;
};

function normalizeColumns<RecordType extends object>(columns: TableProps<RecordType>['columns']) {
  if (!columns) {
    return columns;
  }

  return columns.map((column) => {
    if (!column) {
      return column;
    }
    return {
      align: column.align ?? 'left',
      ellipsis: column.ellipsis ?? true,
      ...column,
    };
  });
}

const { Title } = Typography;

const List = forwardRef(<TRow, TRequest = any, TStats = any>(props: ListProps<TRow, TRequest, TStats>, ref: React.Ref<ListRef>) => {
  const { title, queryFields, fetcher, buildParams, defaultQuery, kpiItems, actions, columns, rowKey, tableProps } = props;
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<TRow[]>([]);
  const [stats, setStats] = useState<TStats | undefined>(undefined);
  const [pageInfo, setPageInfo] = useState<PaginationInfo>({ page: 1, page_size: 20, total: 0, total_pages: 0 });
  const [lastQuery, setLastQuery] = useState<ForgeQuery>({});

  const doFetch = async (params: TRequest) => {
    setLoading(true);
    try {
      const result = await fetcher(params);
      setRows((result?.rows || []) as TRow[]);
      setStats(result?.stats);
      if (result?.pagination) setPageInfo(result.pagination);
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    reload: async () => {
      const merged = { ...lastQuery, page: pageInfo.page, pageSize: pageInfo.page_size } as any;
      const params = (buildParams ? buildParams(merged) : (merged as unknown as TRequest));
      await doFetch(params);
    },
    search: async (q: ForgeQuery) => {
      await onSearch(q);
    }
  }));

  useEffect(() => {
    onSearch(defaultQuery || {});
  }, []);

  const kpis = useMemo(() => {
    if (!kpiItems) return undefined;
    return typeof kpiItems === 'function' ? kpiItems(stats as any) : kpiItems;
  }, [kpiItems, stats]);

  const onSearch = async (q: ForgeQuery) => {
    setLastQuery(q);
    const merged = { ...q, page: 1, pageSize: pageInfo.page_size } as any; // Reset to page 1 on search
    const params = (buildParams ? buildParams(merged) : (merged as unknown as TRequest));
    await doFetch(params);
  };

  return (
    <div style={{ padding: 24 }}>
      {kpis && kpis.length ? <StatsKPIGroup items={kpis as any} /> : null}
      <div style={{ marginTop: 12 }}>
        <ForgeQueryBuilder fields={queryFields} onSearch={onSearch} defaultQuery={defaultQuery} />
      </div>
      {/* actions 暂不实现，但保留接口以兼容未来扩展 */}
      <div style={{ marginTop: 16 }}>
        <Card key={'table'} title={title} style={{ marginTop: 16 }}>
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={rows}
              rowKey={rowKey as any}
              pagination={{ 
                current: pageInfo.page, 
                pageSize: pageInfo.page_size, 
                total: pageInfo.total, 
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条记录`,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                onChange: async (p, ps) => { 
                  // If page size changes, reset to page 1? Or keep current? 
                  // Usually if page size changes, we might want to stay around the same record or reset.
                  // Default behavior: just update.
                  const newPage = ps !== pageInfo.page_size ? 1 : p;
                  setPageInfo(prev => ({ ...prev, page: newPage, page_size: ps || prev.page_size })); 
                  const next = { ...lastQuery, page: newPage, pageSize: ps || pageInfo.page_size } as any; 
                  const params = (buildParams ? buildParams(next) : (next as unknown as TRequest)); 
                  await doFetch(params); 
                } 
              }}
              {...(tableProps || {})}
            />
          </Spin>
        </Card>
      </div>
    </div>
  );
}) as <TRow, TRequest = any, TStats = any>(props: ListProps<TRow, TRequest, TStats> & { ref?: React.Ref<ListRef> }) => React.ReactElement;


export default List;
